/**
 * The Review footer on a ticket with a pull request (simplification,
 * 2026-09-24). Such a ticket carries no open gate: Proctor's approval took
 * the draft out of draft, and the merge webhook, not a click here, moves it to
 * Done. The footer reads:
 *
 *   MERGE   the consequence   [Request changes] [Merge | Merge on GitHub]
 *
 * "Merge" only when the ticket read says this reader may merge in YAGNI
 * (`canMergeInApp`: the workspace's switch is on and they own the Team or
 * supervise one of its lines); otherwise the primary is a link to the pull
 * request on GitHub. Request changes starts a fix round with the reader's
 * notes (POST /api/work-items/:id/request-changes): no gate is open to answer,
 * so the round rides the ticket's own fix-round line.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  WorkItemGitHubState,
  WorkItemMergeAccess,
  WorkItemPrConflict,
  WorkItemPrConflictFixer,
  WorkItemPrMainMerged,
  WorkItemPullRequestReview,
  WorkItemSettleSummary,
} from "@yagni/shared";
import { FootLead } from "./CaseDock.js";
import { useResolveWorkItemGate } from "../tracker/useWorkItemGate.js";
import {
  DRAFT_HELD_UNVERIFIED_CONSEQUENCE,
  DRAFT_WAIT_CONSEQUENCE,
  GATE_LABEL,
  HELD_APPROVAL_CONFIRMED,
  HELD_APPROVAL_KEPT_DRAFT,
  HELD_APPROVAL_CONFIRMING,
  HELD_APPROVAL_CONSEQUENCE,
  HELD_APPROVAL_ELSEWHERE,
  HELD_APPROVAL_FAILED,
  HELD_APPROVAL_LABEL,
  HELD_APPROVAL_READ,
  HELD_APPROVAL_STAMP,
  HELD_APPROVAL_VERB,
  MERGE_CONSEQUENCE,
  MERGE_FAILED,
  MERGE_MERGING,
  MERGE_NO_PR,
  MERGE_ON_GITHUB,
  MERGE_ON_GITHUB_CONSEQUENCE,
  MERGE_STAMP,
  MERGE_UNREADABLE_STATUS_NOTE,
  MERGE_VERB,
  mergedLine,
  REQUEST_CHANGES_FAILED,
  REQUEST_CHANGES_PLACEHOLDER,
  REQUEST_CHANGES_SENT,
  REQUEST_CHANGES_STAMP,
  REQUEST_CHANGES_VERB,
  REVIEW_CONFIRM_NOT_POSTED_FAILED,
  REVIEW_CONFIRM_NOT_POSTED_STALE,
  REVIEW_CONFIRM_NOTHING_KEPT,
} from "./workCopy.js";

/** The merge facts plus the pull request's review facts, from the same ticket read. */
export type MergeFacts = WorkItemMergeAccess & WorkItemPullRequestReview;

function readHeldReview(value: unknown): WorkItemPullRequestReview["heldReview"] {
  if (!value || typeof value !== "object") return null;
  const { caseId, gateId } = value as { caseId?: unknown; gateId?: unknown };
  return typeof caseId === "string" && typeof gateId === "string" ? { caseId, gateId } : null;
}

function readGitHubState(value: unknown): WorkItemGitHubState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const state = value as Partial<WorkItemGitHubState>;
  if (typeof state.readAt !== "string") return null;
  const decision = state.reviewDecision;
  return {
    reviewDecision:
      decision === "APPROVED" || decision === "CHANGES_REQUESTED" || decision === "REVIEW_REQUIRED"
        ? decision
        : null,
    approvedBy: Array.isArray(state.approvedBy)
      ? state.approvedBy.filter((login) => typeof login === "string")
      : [],
    mergeableState: typeof state.mergeableState === "string" ? state.mergeableState : null,
    openThreads: typeof state.openThreads === "number" ? state.openThreads : 0,
    resolvedThreads: typeof state.resolvedThreads === "number" ? state.resolvedThreads : 0,
    readAt: state.readAt,
    stale: state.stale === true,
    reviewAppApproved: state.reviewAppApproved === true,
  };
}

const CONFLICT_FIXERS: ReadonlySet<string> = new Set<WorkItemPrConflictFixer>([
  "wright",
  "window_passed",
  "merge_net",
  "ask_open",
  "person",
]);

function readConflict(value: unknown): WorkItemPrConflict | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const conflict = value as Partial<WorkItemPrConflict>;
  if (typeof conflict.fixer !== "string" || !CONFLICT_FIXERS.has(conflict.fixer)) return null;
  return {
    since: typeof conflict.since === "string" ? conflict.since : null,
    base: typeof conflict.base === "string" && conflict.base ? conflict.base : null,
    fixer: conflict.fixer,
    roundCost: typeof conflict.roundCost === "string" ? conflict.roundCost : null,
  };
}

function readMainMerged(value: unknown): WorkItemPrMainMerged | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const merged = value as Partial<WorkItemPrMainMerged>;
  if (typeof merged.at !== "string") return null;
  return {
    at: merged.at,
    base: typeof merged.base === "string" && merged.base ? merged.base : null,
    approvalDismissed: merged.approvalDismissed === true,
  };
}

function readSettleSummary(value: unknown): WorkItemSettleSummary | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const summary = value as Partial<WorkItemSettleSummary>;
  if (typeof summary.settledAt !== "string") return null;
  return {
    fixed: typeof summary.fixed === "number" ? summary.fixed : 0,
    disputed: typeof summary.disputed === "number" ? summary.disputed : 0,
    needsPerson: Array.isArray(summary.needsPerson) ? summary.needsPerson : [],
    disagreements: Array.isArray(summary.disagreements) ? summary.disagreements : [],
    leftover: typeof summary.leftover === "number" ? summary.leftover : 0,
    settledAt: summary.settledAt,
  };
}

/**
 * The ticket read's merge facts (`GET /api/work-items/:id`). The list read
 * does not carry them, so the case file asks once per ticket it shows in
 * Review. Null while the read is out; a failed read reads as "merge on GitHub"
 * at the pull request the list already knows, never as a Merge button. An
 * older server sends no draft facts, which read as null: today's footer.
 */
export function useMergeAccess(
  workItemId: string,
  enabled: boolean,
  fallbackPrUrl: string | null,
  /**
   * A change here reads the facts again: the review or a fix round moved, and
   * the draft state and the settle summary with it. The first read is the
   * one-shot it always was.
   */
  refreshKey = "",
): MergeFacts | null {
  const [access, setAccess] = useState<MergeFacts | null>(null);
  useEffect(() => {
    if (!enabled) {
      setAccess(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<Partial<MergeFacts>>) : null))
      .then((body) => {
        if (cancelled) return;
        setAccess({
          canMergeInApp: body?.canMergeInApp === true,
          pullRequestUrl:
            typeof body?.pullRequestUrl === "string" ? body.pullRequestUrl : fallbackPrUrl,
          pullRequestDraft:
            typeof body?.pullRequestDraft === "boolean" ? body.pullRequestDraft : null,
          heldReview: readHeldReview(body?.heldReview),
          heldReviewElsewhere: body?.heldReviewElsewhere === true,
          unreadableStatus: body?.unreadableStatus === true,
          draftHeldUnverified: body?.draftHeldUnverified === true,
          github: readGitHubState(body?.github),
          settleSummary: readSettleSummary(body?.settleSummary),
          conflict: readConflict(body?.conflict),
          mainMerged: readMainMerged(body?.mainMerged),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAccess({
            canMergeInApp: false,
            pullRequestUrl: fallbackPrUrl,
            pullRequestDraft: null,
            heldReview: null,
            heldReviewElsewhere: false,
            github: null,
            settleSummary: null,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled, fallbackPrUrl, refreshKey]);
  return access;
}

export function MergeFooter({
  workItemId,
  access,
  onSettled,
}: {
  workItemId: string;
  access: WorkItemMergeAccess & Pick<WorkItemPullRequestReview, "unreadableStatus">;
  /** A merge or a request landed: the page keeps the stamp and refetches. */
  onSettled: (settled: { stamp: string; text: string }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  const merge = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/merge`, {
        method: "POST",
        credentials: "include",
      });
      const body = (await res.json().catch(() => null)) as {
        mergeCommitSha?: unknown;
        error?: unknown;
      } | null;
      if (!res.ok) {
        // The route's refusal is a sentence written for this footer.
        setError(typeof body?.error === "string" && body.error.trim() ? body.error : MERGE_FAILED);
        return;
      }
      onSettled({
        stamp: MERGE_STAMP,
        text: mergedLine(typeof body?.mergeCommitSha === "string" ? body.mergeCommitSha : null),
      });
    } catch {
      setError(MERGE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  // One key per request, so a retry after a dropped response never buys a
  // second fix round.
  const [requestId] = useState(() => crypto.randomUUID());

  // The first press opens the box; the next, with words in it, starts a fix
  // round with them: Wright revises this pull request, and Proctor reviews it
  // again.
  const requestChanges = async () => {
    if (!note.trim()) {
      setNoteOpen(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/request-changes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: note.trim(), requestId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
        // The route's refusal is a sentence written for this footer.
        setError(
          typeof body?.error === "string" && body.error.trim()
            ? body.error
            : REQUEST_CHANGES_FAILED,
        );
        return;
      }
      onSettled({ stamp: REQUEST_CHANGES_STAMP, text: REQUEST_CHANGES_SENT });
    } catch {
      setError(REQUEST_CHANGES_FAILED);
    } finally {
      setBusy(false);
    }
  };

  const consequence = access.canMergeInApp
    ? MERGE_CONSEQUENCE
    : access.pullRequestUrl
      ? MERGE_ON_GITHUB_CONSEQUENCE
      : MERGE_NO_PR;

  return (
    <div className="wk-gate wk-gatefoot" data-testid="wk-merge-footer">
      {noteOpen && (
        <textarea
          className="wk-gate-input"
          autoFocus
          rows={2}
          value={note}
          placeholder={REQUEST_CHANGES_PLACEHOLDER}
          aria-label={REQUEST_CHANGES_PLACEHOLDER}
          onChange={(e) => setNote(e.target.value)}
          data-testid="wk-merge-note"
        />
      )}
      <div className="feed-story-action-foot">
        <div className="deccard-foot-start">
          <FootLead label={GATE_LABEL.merge} consequence={consequence} />
        </div>
        <button
          type="button"
          className="feed-act-decline"
          disabled={busy}
          onClick={() => void requestChanges()}
          data-testid="wk-request-changes"
        >
          {REQUEST_CHANGES_VERB}
        </button>
        {access.canMergeInApp ? (
          <button
            type="button"
            className="feed-act-approve"
            disabled={busy}
            onClick={() => void merge()}
            data-testid="wk-merge"
          >
            {busy ? MERGE_MERGING : MERGE_VERB}
          </button>
        ) : access.pullRequestUrl ? (
          <a
            className="feed-act-approve"
            href={access.pullRequestUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="wk-merge-on-github"
          >
            {MERGE_ON_GITHUB}
          </a>
        ) : null}
      </div>
      {access.unreadableStatus && (
        <p className="wk-gate-note" data-testid="wk-merge-unreadable-status">
          {MERGE_UNREADABLE_STATUS_NOTE}
        </p>
      )}
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Proctor approved this push, and the review line holds the approval for a
 * person (any rung but autonomous). The confirm lives on the review case; this
 * footer resolves that same gate from the build ticket, so the person who
 * merges is told the draft is waiting on them. No merge verbs: the pull
 * request is still a draft until the confirm posts.
 */
export function HeldReviewFooter({
  held,
  onSettled,
}: {
  held: { caseId: string; gateId: string };
  onSettled: (settled: { stamp: string; text: string }) => void;
}) {
  const { resolveGate, isResolving } = useResolveWorkItemGate();
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setError(null);
    try {
      const resolved = await resolveGate({
        workItemId: held.caseId,
        gateId: held.gateId,
        action: "accept_result",
      });
      // The route reports the real delivery: never claim "posted" over a post
      // that was skipped or failed.
      const delivery = resolved?.reviewDelivery;
      const text =
        delivery && !delivery.posted
          ? delivery.reason === "pr_closed" || delivery.reason === "head_moved"
            ? REVIEW_CONFIRM_NOT_POSTED_STALE
            : delivery.reason === "nothing_kept"
              ? REVIEW_CONFIRM_NOTHING_KEPT
              : REVIEW_CONFIRM_NOT_POSTED_FAILED
          : delivery?.heldInDraft
            ? HELD_APPROVAL_KEPT_DRAFT
            : HELD_APPROVAL_CONFIRMED;
      onSettled({ stamp: HELD_APPROVAL_STAMP, text });
    } catch (err) {
      // The route's refusal is a sentence written for a person.
      setError(err instanceof Error && err.message.trim() ? err.message : HELD_APPROVAL_FAILED);
    }
  };

  return (
    <div className="wk-gate wk-gatefoot" data-testid="wk-held-review-footer">
      <div className="feed-story-action-foot">
        <div className="deccard-foot-start">
          <FootLead
            label={HELD_APPROVAL_LABEL}
            consequence={
              <>
                {HELD_APPROVAL_CONSEQUENCE}{" "}
                <Link to={`/work?workItem=${encodeURIComponent(held.caseId)}`}>
                  {HELD_APPROVAL_READ}
                </Link>
              </>
            }
          />
        </div>
        <button
          type="button"
          className="feed-act-approve"
          disabled={isResolving}
          onClick={() => void confirm()}
          data-testid="wk-held-confirm"
        >
          {isResolving ? HELD_APPROVAL_CONFIRMING : HELD_APPROVAL_VERB}
        </button>
      </div>
      {error && (
        <p className="wk-gate-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A draft this reader cannot move: its checks or review are still out, it
 * was published from a build that did not pass everything, or Proctor's
 * approval waits on a review case only someone else can open. Either way
 * there is nothing to merge yet, and the line says which.
 */
export function DraftWaitFooter({
  heldElsewhere,
  heldUnverified = false,
}: {
  heldElsewhere: boolean;
  heldUnverified?: boolean;
}) {
  return (
    <div
      className="wk-gate wk-gatefoot"
      data-testid={heldElsewhere ? "wk-held-elsewhere" : "wk-draft-wait"}
    >
      <div className="feed-story-action-foot">
        <div className="deccard-foot-start">
          <FootLead
            label={heldElsewhere ? HELD_APPROVAL_LABEL : GATE_LABEL.merge}
            consequence={
              heldElsewhere
                ? HELD_APPROVAL_ELSEWHERE
                : heldUnverified
                  ? DRAFT_HELD_UNVERIFIED_CONSEQUENCE
                  : DRAFT_WAIT_CONSEQUENCE
            }
          />
        </div>
      </div>
    </div>
  );
}
